import React, { useState, useEffect } from 'react';
import { appointmentReminderSettingsService } from '../services/api';
import { doctorService, locationService } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import {
  PlusIcon,
  ReminderIcon as EditIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon
} from '../assets/icons';

const AppointmentReminderSettings = () => {
  const { t, currentLanguage } = useLanguage();
  const [settings, setSettings] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editingSettingId, setEditingSettingId] = useState(null);
  const [filters, setFilters] = useState({
    location_id: '',
    doctor_id: '',
    enabled: null
  });

  const [formData, setFormData] = useState({
    timezone: 'Asia/Hong_Kong',
    location_id: '',
    doctor_id: '',
    enabled: false,
    reminder_days_before: 1,
    cutoff_reminder_at: '17:00'
  });

  // Common timezones
  const timezones = [
    'Asia/Hong_Kong',
    'Asia/Singapore',
    'Asia/Shanghai',
    'Asia/Tokyo',
    'UTC'
  ];

  useEffect(() => {
    fetchSettings();
    fetchDoctors();
    fetchLocations();
  }, [filters]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await appointmentReminderSettingsService.getAppointmentReminderSettings(filters);
      setSettings(response.items || []);
    } catch (err) {
      console.error('Error fetching appointment reminder settings:', err);
      setError(t('appointmentReminderSettings.error.loadFailed', 'Failed to load appointment reminder settings'));
    } finally {
      setLoading(false);
    }
  };

  const fetchDoctors = async () => {
    try {
      const response = await doctorService.getDoctors();
      setDoctors(response.doctors || []);
    } catch (err) {
      console.error('Error fetching doctors:', err);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await locationService.getLocations();
      setLocations(response.items || []);
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value === '' ? null : value
    }));
  };

  const handleAddNew = () => {
    setFormData({
      timezone: 'Asia/Hong_Kong',
      location_id: '',
      doctor_id: '',
      enabled: false,
      reminder_days_before: 1,
      cutoff_reminder_at: '17:00'
    });
    setEditingSettingId(null);
    setIsEditing(true);
    setError(null);
  };

  const handleEdit = (setting) => {
    setFormData({
      timezone: setting.timezone || 'Asia/Hong_Kong',
      location_id: setting.location_id ? String(setting.location_id) : '',
      doctor_id: setting.doctor_id ? String(setting.doctor_id) : '',
      enabled: setting.enabled || false,
      reminder_days_before: setting.reminder_days_before || 1,
      cutoff_reminder_at: setting.cutoff_reminder_at || '17:00'
    });
    setEditingSettingId(setting.id);
    setIsEditing(true);
    setError(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingSettingId(null);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate cutoff_reminder_at format (HH:MM)
    const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
    if (!timeRegex.test(formData.cutoff_reminder_at)) {
      setError(t('appointmentReminderSettings.error.invalidTimeFormat', 'Cutoff reminder time must be in HH:MM format (e.g., 17:00)'));
      return;
    }

    try {
      setLoading(true);
      const submitData = {
        timezone: formData.timezone,
        location_id: formData.location_id || null,
        doctor_id: formData.doctor_id || null,
        enabled: formData.enabled,
        reminder_days_before: parseInt(formData.reminder_days_before),
        cutoff_reminder_at: formData.cutoff_reminder_at
      };

      if (editingSettingId) {
        await appointmentReminderSettingsService.updateAppointmentReminderSetting(editingSettingId, submitData);
        setSuccessMessage(t('appointmentReminderSettings.success.updated', 'Appointment reminder setting updated successfully'));
      } else {
        await appointmentReminderSettingsService.createAppointmentReminderSetting(submitData);
        setSuccessMessage(t('appointmentReminderSettings.success.created', 'Appointment reminder setting created successfully'));
      }

      await fetchSettings();
      setIsEditing(false);
      setEditingSettingId(null);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving appointment reminder setting:', err);
      setError(err.response?.data?.detail || t('appointmentReminderSettings.error.saveFailed', 'Failed to save appointment reminder setting'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (settingId) => {
    if (!window.confirm(t('appointmentReminderSettings.confirm.delete', 'Are you sure you want to delete this appointment reminder setting?'))) {
      return;
    }

    try {
      setLoading(true);
      await appointmentReminderSettingsService.deleteAppointmentReminderSetting(settingId);
      setSuccessMessage(t('appointmentReminderSettings.success.deleted', 'Appointment reminder setting deleted successfully'));
      await fetchSettings();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting appointment reminder setting:', err);
      setError(err.response?.data?.detail || t('appointmentReminderSettings.error.deleteFailed', 'Failed to delete appointment reminder setting'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (settingId) => {
    try {
      setLoading(true);
      await appointmentReminderSettingsService.toggleAppointmentReminderSettingEnabled(settingId);
      setSuccessMessage(t('appointmentReminderSettings.success.toggled', 'Appointment reminder setting status toggled successfully'));
      await fetchSettings();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error toggling appointment reminder setting:', err);
      setError(err.response?.data?.detail || t('appointmentReminderSettings.error.toggleFailed', 'Failed to toggle appointment reminder setting'));
    } finally {
      setLoading(false);
    }
  };

  const getDoctorName = (doctorId) => {
    if (!doctorId) return t('appointmentReminderSettings.notAvailable', 'N/A');
    const doctor = doctors.find(d => d.id === doctorId);
    return doctor ? `${doctor.primary_name} (${doctor.speciality})` : doctorId;
  };

  const getLocationName = (locationId) => {
    if (!locationId) return t('appointmentReminderSettings.notAvailable', 'N/A');
    const location = locations.find(l => l.id === locationId);
    if (!location) return locationId;

    // Try to get i18n name first, fallback to primary_name
    const i18nName = location.meta_data?.i18n?.name?.[currentLanguage];
    return i18nName || location.primary_name;
  };

  const getLocationDisplayName = (location) => {
    // Try to get i18n name first, fallback to primary_name
    const i18nName = location.meta_data?.i18n?.name?.[currentLanguage];
    return i18nName || location.primary_name;
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t('appointmentReminderSettings.title', 'Appointment Reminder Settings')}
            </h1>
            <p className="text-gray-600 mt-1">
              {t('appointmentReminderSettings.subtitle', 'Manage appointment reminder settings for your clinic')}
            </p>
          </div>
          {!isEditing && (
            <button
              onClick={handleAddNew}
              className="px-4 py-2 bg-[#075e54] text-white rounded-md hover:bg-[#064e45] flex items-center"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              {t('appointmentReminderSettings.addNew', 'Add New Setting')}
            </button>
          )}
        </div>

        <div className="p-6">
          {successMessage && (
            <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
              {successMessage}
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
              {error}
            </div>
          )}

          {/* Filters */}
          {!isEditing && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                {t('appointmentReminderSettings.filters', 'Filters')}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('appointmentReminderSettings.location', 'Location')}
                  </label>
                  <select
                    name="location_id"
                    value={filters.location_id || ''}
                    onChange={handleFilterChange}
                    className="w-full p-2 border rounded"
                  >
                    <option value="">{t('appointmentReminderSettings.allLocations', 'All Locations')}</option>
                    {locations.map(location => (
                      <option key={location.id} value={String(location.id)}>
                        {getLocationDisplayName(location)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('appointmentReminderSettings.doctor', 'Doctor')}
                  </label>
                  <select
                    name="doctor_id"
                    value={filters.doctor_id || ''}
                    onChange={handleFilterChange}
                    className="w-full p-2 border rounded"
                  >
                    <option value="">{t('appointmentReminderSettings.allDoctors', 'All Doctors')}</option>
                    {doctors.map(doctor => (
                      <option key={doctor.id} value={doctor.id}>
                        {doctor.primary_name} - {doctor.speciality}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('appointmentReminderSettings.enabledStatus', 'Enabled Status')}
                  </label>
                  <select
                    name="enabled"
                    value={filters.enabled === null ? '' : filters.enabled.toString()}
                    onChange={(e) => setFilters(prev => ({
                      ...prev,
                      enabled: e.target.value === '' ? null : e.target.value === 'true'
                    }))}
                    className="w-full p-2 border rounded"
                  >
                    <option value="">{t('appointmentReminderSettings.all', 'All')}</option>
                    <option value="true">{t('appointmentReminderSettings.enabled', 'Enabled')}</option>
                    <option value="false">{t('appointmentReminderSettings.disabled', 'Disabled')}</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() => setFilters({ location_id: '', doctor_id: '', enabled: null })}
                    className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  >
                    {t('appointmentReminderSettings.clearFilters', 'Clear Filters')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          {isEditing && (
            <div className="mb-6 p-6 border rounded-lg bg-gray-50">
              <h4 className="text-lg font-medium mb-4">
                {editingSettingId ? t('appointmentReminderSettings.editSetting', 'Edit Setting') : t('appointmentReminderSettings.addNewSetting', 'Add New Setting')}
              </h4>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('appointmentReminderSettings.timezone', 'Timezone')} <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="timezone"
                      value={formData.timezone}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded"
                      required
                    >
                      {timezones.map(tz => (
                        <option key={tz} value={tz}>{tz}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('appointmentReminderSettings.location', 'Location')}
                    </label>
                    <select
                      name="location_id"
                      value={formData.location_id}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded"
                    >
                      <option value="">{t('appointmentReminderSettings.allLocationsNoSpecific', 'All Locations (No specific location)')}</option>
                      {locations.map(location => (
                        <option key={location.id} value={String(location.id)}>
                          {getLocationDisplayName(location)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('appointmentReminderSettings.doctor', 'Doctor')}
                    </label>
                    <select
                      name="doctor_id"
                      value={formData.doctor_id}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded"
                    >
                      <option value="">{t('appointmentReminderSettings.allDoctorsNoSpecific', 'All Doctors (No specific doctor)')}</option>
                      {doctors.map(doctor => (
                        <option key={doctor.id} value={doctor.id}>
                          {doctor.primary_name} - {doctor.speciality}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('appointmentReminderSettings.reminderDaysBefore', 'Reminder Days Before')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="reminder_days_before"
                      value={formData.reminder_days_before}
                      onChange={handleInputChange}
                      min="1"
                      max="30"
                      className="w-full p-2 border rounded"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {t('appointmentReminderSettings.reminderDaysBeforeHint', 'Number of days before appointment to send reminder')}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('appointmentReminderSettings.cutoffReminderAt', 'Cutoff Reminder At')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="time"
                      name="cutoff_reminder_at"
                      value={formData.cutoff_reminder_at}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {t('appointmentReminderSettings.cutoffReminderAtHint', 'Time cutoff for sending reminders (HH:MM format)')}
                    </p>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="enabled"
                      checked={formData.enabled}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-[#075e54] border-gray-300 rounded"
                    />
                    <label className="ml-2 text-sm font-medium text-gray-700">
                      {t('appointmentReminderSettings.enabled', 'Enabled')}
                    </label>
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  >
                    {t('common.cancel', 'Cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-[#075e54] text-white rounded hover:bg-[#064e45] disabled:opacity-50"
                  >
                    {loading ? t('common.saving', 'Saving...') : (editingSettingId ? t('common.update', 'Update') : t('common.create', 'Create'))}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Settings List */}
          {!isEditing && (
            <div>
              {loading ? (
                <p className="text-gray-500">{t('common.loading', 'Loading...')}</p>
              ) : settings.length === 0 ? (
                <p className="text-gray-500">{t('appointmentReminderSettings.noSettings', 'No appointment reminder settings found.')}</p>
              ) : (
                <div className="space-y-4">
                  {settings.map(setting => (
                    <div key={setting.id} className="border rounded-lg p-4 bg-white">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h5 className="font-medium text-gray-700">
                            {t('appointmentReminderSettings.setting', 'Setting')} {setting.id.substring(0, 8)}
                            {setting.enabled ? (
                              <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full flex items-center inline-flex">
                                <CheckCircleIcon className="h-3 w-3 mr-1" />
                                {t('appointmentReminderSettings.enabled', 'Enabled')}
                              </span>
                            ) : (
                              <span className="ml-2 px-2 py-0.5 text-xs bg-gray-100 text-gray-800 rounded-full flex items-center inline-flex">
                                <XCircleIcon className="h-3 w-3 mr-1" />
                                {t('appointmentReminderSettings.disabled', 'Disabled')}
                              </span>
                            )}
                          </h5>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleToggleEnabled(setting.id)}
                            className={`px-3 py-1 text-xs rounded ${
                              setting.enabled
                                ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                                : 'bg-green-500 text-white hover:bg-green-600'
                            }`}
                          >
                            {setting.enabled ? t('appointmentReminderSettings.disable', 'Disable') : t('appointmentReminderSettings.enable', 'Enable')}
                          </button>
                          <button
                            onClick={() => handleEdit(setting)}
                            className="px-3 py-1 text-xs bg-[#075e54] text-white rounded hover:bg-[#064e45] flex items-center"
                          >
                            <EditIcon className="h-3 w-3 mr-1" />
                            {t('common.edit', 'Edit')}
                          </button>
                          <button
                            onClick={() => handleDelete(setting.id)}
                            className="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 flex items-center"
                          >
                            <TrashIcon className="h-3 w-3 mr-1" />
                            {t('common.delete', 'Delete')}
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">{t('appointmentReminderSettings.timezone', 'Timezone')}:</span>
                          <span className="ml-2 font-medium">{setting.timezone}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">{t('appointmentReminderSettings.location', 'Location')}:</span>
                          <span className="ml-2 font-medium">{getLocationName(setting.location_id)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">{t('appointmentReminderSettings.doctor', 'Doctor')}:</span>
                          <span className="ml-2 font-medium">{getDoctorName(setting.doctor_id)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">{t('appointmentReminderSettings.daysBefore', 'Days Before')}:</span>
                          <span className="ml-2 font-medium">{setting.reminder_days_before}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">{t('appointmentReminderSettings.cutoffTime', 'Cutoff Time')}:</span>
                          <span className="ml-2 font-medium">{setting.cutoff_reminder_at}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppointmentReminderSettings;

